/** guardian/family — API. Authenticated guardian endpoints (JWT via http). */
import { http } from '../../_shared/http';
import type {
  CreateStudentPayload,
  FamilyDevice,
  FamilyOverview,
  FamilyStudent,
  InviteGuardianPayload,
  LinkDeviceResponse,
  UpdateGuardianProfilePayload,
  UpdateStudentPayload,
} from './types';

/** Appends ?familyAccountId= for admin calls (guardians act on their own family) */
const familyQuery = (familyAccountId?: string) =>
  familyAccountId ? `?familyAccountId=${encodeURIComponent(familyAccountId)}` : '';

export const guardianFamilyApi = {
  getMyFamily: () => http.get<FamilyOverview>('/family/my'),

  updateGuardianProfile: (payload: UpdateGuardianProfilePayload) =>
    http.patch<FamilyOverview>('/family/guardian-profile', payload),

  /** Uploads the calling guardian's own profile photo */
  uploadGuardianPhoto: (blob: Blob) => {
    const formData = new FormData();
    formData.append('file', blob, 'profile.png');
    return http.post<FamilyOverview>('/family/guardian-profile/photo', formData);
  },

  /** Invites another guardian to the family (emails a setup link) */
  inviteGuardian: (payload: InviteGuardianPayload) =>
    http.post<FamilyOverview>('/family/guardians/invite', payload),

  /** Deactivates a guardian (not yourself, not the last active one) */
  removeGuardian: (id: string, familyAccountId?: string) =>
    http.del<FamilyOverview>(`/family/guardians/remove/${id}${familyQuery(familyAccountId)}`),

  createStudent: (payload: CreateStudentPayload) =>
    http.post<FamilyStudent>('/family/students/create', payload),

  updateStudent: (id: string, payload: UpdateStudentPayload, familyAccountId?: string) =>
    http.patch<FamilyStudent>(`/family/students/update/${id}${familyQuery(familyAccountId)}`, payload),

  removeStudent: (id: string, familyAccountId?: string) =>
    http.del<{ message: string }>(`/family/students/delete/${id}${familyQuery(familyAccountId)}`),

  uploadStudentPhoto: (id: string, blob: Blob, familyAccountId?: string) => {
    const formData = new FormData();
    formData.append('file', blob, 'profile.png');
    return http.post<FamilyStudent>(
      `/family/students/${id}/profile-photo${familyQuery(familyAccountId)}`,
      formData,
    );
  },

  linkDevice: (name: string) => http.post<LinkDeviceResponse>('/family/devices/link', { name }),

  listDevices: (familyAccountId?: string) =>
    http.get<FamilyDevice[]>(`/family/devices/list${familyQuery(familyAccountId)}`),

  revokeDevice: (id: string, familyAccountId?: string) =>
    http.del<{ message: string }>(`/family/devices/revoke/${id}${familyQuery(familyAccountId)}`),
};
